/*
 * Copyright (C) 2025 Musarubra US LLC.  All Rights Reserved.
 */

import { NonWidgetPage } from './non-widget-page-base';
import { iamLogin, iamUtils, ConsoleApp } from '../main';
import { Invites } from '../invites';
import { iamLocalization } from '../localization';
import { iam, DecodedToken } from '../iam';

type DashboardConsoleApp = ConsoleApp & { description?: string, imageUrl?: string, target?: string };

export class DashboardPage extends NonWidgetPage {
  public override localize(): void {
    super.localize();
    iamLocalization.addLocalization(() => {
      // not localizing 'main-header' here because it's based on user name which is pulled out of the access token, so this gets localized via localization callback
      iamLogin.localizeElement('companyLabel', 'selectTenant');
      iamLogin.localizeElement('managementConsoleLabel', 'selectProduct');
      iamLogin.localizeElement('noConsolesText', 'dashboardApplicationNone');
      iamLogin.localizeElement('subscriptionText', {
        stringKey: 'subscriptionText',
        stringParameters: {
          startLink: `<a target="_blank" href="${iam.settings.subscription + iamLogin.getFlagPassOn()}">`,
          endLink: '</a>'
        },
        rawHTML: true
      });
      iamLogin.localizeElement('userProfile', 'userProfile');
      iamLogin.localizeElement('manageUsers', 'manageUsers');
      iamLogin.localizeElement('subscription', 'subscription');
      iamLogin.localizeElement('logOff', 'logOff');
      iamLogin.localizeElement('pageTitle', 'dashboard');
      iamLogin.localizeElement('idpConfig', 'idpConfig');
      iamLogin.localizeElement('applianceRegistration', 'applianceRegistration');
      iamLogin.localizeElement('clientCredentials', 'clientCredentials');
      iamLogin.localizeElement('auditLog', 'auditLog');
      iamLogin.localizeElement('organizationAccount', 'organizationAccount');
      iamLogin.localizeElement('inv-info', 'inviteModalInfo');
      iamLogin.localizeElement('invite-continue', 'inviteModalContinueButton');
      iamLogin.localizeElement('inv-title', 'inviteTitle');
      iamLogin.localizeElement('invite-accept-all', 'inviteAcceptAll');
      iamLogin.localizeElement('invite-decline-all', 'inviteDeclineAll');
      iamLogin.localizeElement('invite-dashboard-button', 'inviteTitle');
      // just overriding one of the tenant widget's strings
      document.querySelector('iam-tenant-list').setAttribute('translations', JSON.stringify({
        'selected-tenant-tooltip': iamLocalization.localize('selectedTenantTooltip', { interpolation: { prefix: '~~disabled~~', suffix: '~~disabled~~' } }) // don't interpolate the '{{id}}' value because the widget will do that
      }));
    });
  }

  public override async initialize() {
    this.addUserActions();
    // do all synchronous initialization first and make rest calls, then final initialization will happen in initializeDashboardPseudoWidget() method
    this.elements.getContainer().classList.add('dashboard');
    if (iam.settings.debug) console.log('page loaded');
    const { accessToken, accessTokenExpires } = await iam.Token.getTokenPromise({ pkceRedirectUrl: iam.settings.tokenHandler, init: true });
    this.getTokenMiddleware(accessToken, accessTokenExpires);
  }

  private addUserActions() {
    const actionsMenu = document.getElementById('userActions');
    const closeMenu = (e: Event) => {
      if (e.isTrusted) {
        // force the popup away momentarily since hovering over the popup otherwise keeps it visible. I was using a timer
        // but that doesn't work in IE or Edge because the ":hover" css selector doesn't clear until the mouse actually
        // moves, so if you select a language and don't move the mouse, the menu would just reappear.
        actionsMenu.style.display = 'none';
        const leave = () => {
          actionsMenu.style.display = '';
          actionsMenu.removeEventListener('mouseleave', leave);
        };
        actionsMenu.addEventListener('mouseleave', leave);
        e.stopImmediatePropagation();
        return false;
      } // else this is a keyboard initiated programmatic click, and the menu should only close if focus goes elsewhere
    };
    document.getElementById('userProfile').addEventListener('click', e => {
      window.open(iam.settings.uamProfile + iamLogin.getFlagPassOn());
      return closeMenu(e);
    });
    document.getElementById('manageUsers').addEventListener('click', e => {
      window.open(iam.settings.uamManagement + iamLogin.getFlagPassOn());
      return closeMenu(e);
    });
    document.getElementById('subscription').addEventListener('click', e => {
      window.open(iam.settings.subscription + iamLogin.getFlagPassOn());
      return closeMenu(e);
    });
    document.getElementById('idpConfig').addEventListener('click', e => {
      window.open(iam.settings.idpConfig + iamLogin.getFlagPassOn());
      return closeMenu(e);
    });
    document.getElementById('applianceRegistration').addEventListener('click', e => {
      window.open(iam.settings.uamRegistration + iamLogin.getFlagPassOn());
      return closeMenu(e);
    });
    document.getElementById('clientCredentials').addEventListener('click', e => {
      window.open(iam.settings.uamClientCreds + iamLogin.getFlagPassOn());
      return closeMenu(e);
    });
    document.getElementById('auditLog').addEventListener('click', e => {
      window.open(iam.settings.uamAuditLog + iamLogin.getFlagPassOn());
      return closeMenu(e);
    });
    document.getElementById('organizationAccount').addEventListener('click', e => {
      window.open(iam.settings.uamOrgAccount + iamLogin.getFlagPassOn());
      return closeMenu(e);
    });
    document.getElementById('logOff').addEventListener('click', e => {
      iam.Token.logout();
      return closeMenu(e);
    });
    document.getElementById('userActionsDropdown').style.display = '';

    const list = actionsMenu.querySelector('ul');
    const navigate = ($event: KeyboardEvent, nextChooser: (index: number) => number) => {
      const prev = list.querySelector<HTMLElement>('.focused');
      const items = Array.from(list.querySelectorAll<HTMLElement>('li > a')).filter(child => child.style.display !== 'none').map(child => child.parentElement);
      let index = prev ? items.indexOf(prev) : -1;
      index = Math.min(Math.max(0, nextChooser(index)), items.length - 1);
      prev?.classList.remove('focused');
      items[index].classList.add('focused');
      $event.preventDefault();
      $event.stopPropagation();
    };
    document.getElementById('userActionsDropdown').addEventListener('keydown', ($event: KeyboardEvent) => {
      const key = $event.key;
      switch (key) {
        case 'ArrowUp':
          navigate($event, index => index - 1);
          break;
        case 'ArrowDown':
          navigate($event, index => index + 1);
          break;
        case 'Home':
          navigate($event, () => 0);
          break;
        case 'End':
          navigate($event, () => 99999);
          break;
        case 'Enter':
          const current = list.querySelector('.focused');
          if (current) {
            current.classList.remove('focused');
            current.querySelector('a').click();
          }
          $event.preventDefault();
          $event.stopPropagation();
      }
    });
    document.getElementById('userActionsDropdown').addEventListener('blur', () => {
      list.querySelector('.focused')?.classList.remove('focused');
    });
  }

  private getTokenMiddleware(accessToken: string, accessTokenExpires: string) {
    try {
      const updateUiForTenant = (parsedToken: DecodedToken) => {
        if (iam.settings.debug) console.log('parsed token: ', parsedToken);
        const scopes = parsedToken.scope.split(' ');
        if (iam.settings.debug) console.log('scopes: ', scopes);

        const partnerUser = !!parsedToken.act?.tenant_id; // partner users can only access the profile page
        const hasAnyScope = (required: string[]) => required.some(one => scopes.includes(one));
        document.getElementById('subscriptionText').style.display = hasAnyScope(['uam:admin']) ? '' : 'none';

        this.displayManageUsers(!partnerUser && hasAnyScope(['uam:admin', 'uam.ui.usr.r', 'uam.ui.usr.w']));
        this.displaySubscription(!partnerUser && hasAnyScope(['uam:admin']));
        this.displayIDPConfig(!partnerUser && hasAnyScope(['uam:admin', 'uam.idp.sa']));

        // these can be unset for any environments that shouldn't show the links
        this.displayApplianceRegistration(!!iam.settings.uamRegistration && !partnerUser && hasAnyScope(['uam:admin', 'uam.srt']));
        this.displayClientCredentials(!!iam.settings.uamClientCreds && !partnerUser && hasAnyScope(['uam:admin', 'uam.cc.r', 'uam.cc.w']));
        this.displayAuditLog(!!iam.settings.uamAuditLog && !partnerUser && hasAnyScope(['uam:admin', 'uam.al.t', 'uam.al.u']));
        this.displayOrganizationAccount(!!iam.settings.uamOrgAccount && !partnerUser && hasAnyScope(['uam:admin']));

        // display the user profile if either the idp claim is not present or it is the mvision idp
        const idp = parsedToken.idp;
        this.displayUserProfile(!idp || idp === iam.settings.mcafeeIdp);
      };
      // helper for getTokenCallBack port to getTokenPromise
      const getTokenMiddlewareCallback = (token: string) => {
        iamLogin.getConsoles(token)
          .then(apps => {
            window.localStorage.setItem('iam:consoleApps', JSON.stringify(apps));
            this.displayApps(apps);
            updateUiForTenant(iam.Token.parseToken(token));
          })
          .catch(reason => {
            if (iam.settings.debug) console.log(reason);
            window.localStorage.removeItem('iam:consoleApps');
            this.displayApps([]);
            updateUiForTenant(iam.Token.parseToken(token));
          });
      };

      iamLogin.registerLocalizationCallback(() => {
        let userName;
        if (iam.Token.getCookie('iam_user_locale') === 'ja_JP') {
          userName = parsedToken.family_name + ' ' + parsedToken.given_name;
        } else {
          userName = parsedToken.given_name + ' ' + parsedToken.family_name;
        }
        iamLogin.localizeElement('main-header', { stringKey: 'whereToGo', stringParameters: { name: userName } });
      }, false);

      this.checkForTokenExchange(); // moving it here so that the flag will be cleared once Authz returns th token.
      const parsedToken = iam.Token.parseToken(accessToken);
      updateUiForTenant(parsedToken);

      const tenantWidget = document.querySelector('iam-tenant-list');
      tenantWidget.addEventListener('select', ((event: CustomEvent<{ id: string, name: string, preventTokenExchange: boolean }>) => {
        document.getElementById('loading-content').style.display = '';
        event.detail.preventTokenExchange = true; // we'll handle token exchange here rather than the widget doing it for us
        iam.Token.tokenExchange(true, event.detail.id);
      }) as EventListener); // casting to EventListener because of tsc compiler issue that lacks a cleaner and concise solution (https://github.com/Microsoft/TypeScript/issues/28357)
      // change event will only happen in other tabs because if the tenant is changed in this tab, the tab will go through some
      // redirects to get the new token. the other tabs need to update their app tiles and user menu (IAM-5637: Tenant switch
      // not reflected across tabs of same origin). if another dashboard tab initiated the tenant switch, the apps list will
      // have been updated, but if any other app switches the tenant, it won't be updated. probably won't need to be done once
      // pkce is official.
      iam.Token.listenForTokenChanges(({ currentToken, previousToken, currentDecodedToken, previousDecodedToken, reason }) => {
        if (currentToken) {
          getTokenMiddlewareCallback(currentToken);
        }
      });
      this.getConsoleList(accessToken);
    } catch (err) {
      console.error(err);
    }
    new Invites().initializeInvites(); // todo: remember this is here now, wasnt being called originally

    // dashboard.html page (which doesn't use widget, hence "pseudo" widget)
    // most of the real work is done in showDashboardPage()
    if (iam.settings.debug) console.log('initializing dashboard widget');
    document.getElementById('pre-okta').classList.add('pre-okta-short');
    document.getElementById('loading-content').style.display = 'none';
    document.getElementById('right-pane-contents').style.display = '';
    this.showCommonFields(['explanation']); // blank but provides proper spacing
    this.initializeCommon();
  }

  private checkForTokenExchange() {
    if (window.localStorage.getItem('tokenExchange') === 'true') {
      if (iam.settings.debug) console.log('Token Exchange Completed');
      window.localStorage.removeItem('tokenExchange');
    }
  }

  private async getConsoleList(token: string) {
    // Attempt to use cached consoleApps list. If nothing in cache, refresh the consoles list
    const consoleApps = JSON.parse(window.localStorage.getItem('iam:consoleApps'));
    if (consoleApps == null) {
      try {
        const apps = await iamLogin.getConsoles(token);
        iamLogin.showWidget();
        this.displayApps(apps);
      } catch (reason) {
        if (iam.settings.debug) console.log(reason);
        iamLogin.showWidget();
        document.getElementById('managementConsoleLabel').style.display = 'none';
        document.getElementById('noConsoles').style.display = 'block';
      }
    } else {
      iamLogin.showWidget();
      this.displayApps(consoleApps);
    }
  }

  private displayIDPConfig(show: boolean) {
    document.getElementById('idpConfig').style.display = show ? 'block' : 'none';
  }

  private displayApplianceRegistration(show: boolean) {
    document.getElementById('applianceRegistration').style.display = show ? 'block' : 'none';
  }

  private displayClientCredentials(show: boolean) {
    document.getElementById('clientCredentials').style.display = show ? 'block' : 'none';
  }

  private displayAuditLog(show: boolean) {
    document.getElementById('auditLog').style.display = show ? 'block' : 'none';
  }

  private displayOrganizationAccount(show: boolean) {
    document.getElementById('organizationAccount').style.display = show ? 'block' : 'none';
  }

  private displayManageUsers(show: boolean) {
    document.getElementById('manageUsers').style.display = show ? 'block' : 'none';
  }

  private displaySubscription(show: boolean) {
    document.getElementById('subscription').style.display = show ? 'block' : 'none';
  }

  private displayUserProfile(show: boolean) {
    document.getElementById('userProfile').style.display = show ? 'block' : 'none';
  }

  private displayApps(apps: DashboardConsoleApp[]) {
    document.getElementById('consoles').innerHTML = '';

    if (apps && apps.length <= 0) {
      document.getElementById('managementConsoleLabel').style.display = 'none';
      document.getElementById('noConsoles').style.display = 'block';
    } else {
      document.getElementById('managementConsoleLabel').style.display = 'block';
      document.getElementById('noConsoles').style.display = 'none';
      apps.forEach(app => {
        if (iam.settings.debug) console.log(app);
        this.createApp(app);
      });
    }
  }

  private async createApp(app: DashboardConsoleApp) {
    const consoleElem = document.createElement('li');
    consoleElem.className = 'console';
    consoleElem.tabIndex = 0;
    consoleElem.setAttribute('data-testid', app.name);
    let redirectURL = app.url;
    try {
      if (iam.settings.debug) console.log('Redirecting to... ' + redirectURL);
      const token = iam.Token.getItem('accessToken');
      if (token && iamLogin.appendTokenUrl(redirectURL)) {
        redirectURL = await iamLogin.getEpoRedirectUrl();
        if (iam.settings.debug) console.log('Modified redirect uri to ' + redirectURL);
      }
    } catch (e) {
    }
    consoleElem.onclick = () => {
      window.open(redirectURL + iamLogin.getFlagPassOn(), app.target === 'blank' ? '_blank' : '_self');
      return false;
    };
    consoleElem.addEventListener('keydown', ($event: KeyboardEvent) => {
      if (['Enter', ' '].includes($event.key)) {
        window.open(redirectURL + iamLogin.getFlagPassOn(), app.target === 'blank' ? '_blank' : '_self');
        $event.preventDefault();
        $event.stopPropagation();
      }
    });

    const imageDiv = document.createElement('div');
    imageDiv.className = 'appImage';
    let appImage;
    if (app.imageUrl) {
      appImage = document.createElement('img');
      appImage.src = app.imageUrl;
      imageDiv.appendChild(appImage);
    }

    const nameDiv = document.createElement('div');
    nameDiv.className = 'appName';
    const displayName = app.description || app.name;
    nameDiv.appendChild(document.createTextNode(displayName));

    consoleElem.appendChild(imageDiv);
    consoleElem.appendChild(nameDiv);
    document.getElementById('consoles').appendChild(consoleElem);
  }
}
